import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { InputNumber as TeaInputNumber, Bubble } from 'tea-component';
import { useIntl } from 'umi';
import { IInputNumberProps } from '../../type';

// 常量定义
const FORM_CONTROLS_SELECTOR = '.tea-form__controls';
const TIP_MARGIN = 40; // 提示语边距（px）
const LINE_WIDTH_RATIO = 0.55; // 计算行宽度时使用的比例（文本总宽度的55%）

/**
 * 测量文本的实际渲染宽度
 * @param textElement - 文本元素引用
 * @returns 文本宽度（px），如果无法测量则返回 null
 */
const measureTextWidth = (textElement: HTMLElement | null): number | null => {
  if (!textElement) {
    return null;
  }

  const measureElement = document.createElement('span');
  measureElement.style.visibility = 'hidden';
  measureElement.style.position = 'absolute';
  measureElement.style.whiteSpace = 'nowrap';

  const computedStyle = window.getComputedStyle(textElement);
  measureElement.style.fontSize = computedStyle.fontSize;
  measureElement.style.fontFamily = computedStyle.fontFamily;
  measureElement.style.fontWeight = computedStyle.fontWeight;
  measureElement.textContent = textElement.textContent || '';

  document.body.appendChild(measureElement);
  const textWidth = measureElement.getBoundingClientRect().width;
  document.body.removeChild(measureElement);

  return textWidth;
};

/**
 * 计算提示语的最大可用宽度
 * @param inputElement - 输入框元素引用
 * @returns 最大宽度（px），如果无法计算则返回 null
 */
const calculateMaxTipWidth = (inputElement: HTMLElement | null): number | null => {
  if (!inputElement) {
    return null;
  }

  const formControlsElement = inputElement.closest(FORM_CONTROLS_SELECTOR);
  if (!formControlsElement) {
    return null;
  }

  const inputWidth = inputElement.getBoundingClientRect().width;
  const formControlsWidth = formControlsElement.getBoundingClientRect().width;
  const calculatedMaxTipWidth = formControlsWidth - inputWidth - TIP_MARGIN;

  return calculatedMaxTipWidth > 0 ? calculatedMaxTipWidth : null;
};

/**
 * 计算实际行宽度，使文本能够平均分成两行
 * @param textWidth - 文本总宽度（px）
 * @param maxWidth - 最大允许宽度（px）
 * @returns 计算出的行宽度（px）
 */
const calculateLineWidth = (textWidth: number, maxWidth: number): number => {
  // 使用文本总宽度的55%作为行宽度，确保文本能够自然换行且两行宽度接近
  return Math.min(textWidth * LINE_WIDTH_RATIO, maxWidth);
};

/**
 * 构建提示语的样式对象
 * @param shouldWrap - 是否需要换行
 * @param actualLineWidth - 实际行宽度（px）
 * @returns 样式对象
 */
const buildTipStyle = (
  shouldWrap: boolean,
  actualLineWidth: number | null,
): React.CSSProperties => {
  if (shouldWrap && actualLineWidth) {
    return {
      width: `${actualLineWidth}px`,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    };
  }

  return {
    width: 'max-content',
    whiteSpace: 'nowrap',
  };
};

export default function InputNumber(props: IInputNumberProps) {
  const { value, field, total: actualMaxValue, isAutoAdjustMaxTipWidth, onChange } = props;
  const { formatMessage } = useIntl();
  const [showMaxValueTip, setShowMaxValueTip] = useState(false);
  const [correctedValue, setCorrectedValue] = useState<number | null>(null);
  const [maxTipWidth, setMaxTipWidth] = useState<number | null>(null);
  const [shouldWrap, setShouldWrap] = useState(false);
  const [actualLineWidth, setActualLineWidth] = useState<number | null>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const tipTextRef = useRef<HTMLDivElement>(null);

  // 动态计算提示语的最大宽度（表单内容区域宽度 - 输入框宽度 - 边距）
  useEffect(() => {
    if (!isAutoAdjustMaxTipWidth) {
      return;
    }

    const updateMaxTipWidth = (): void => {
      const calculatedWidth = calculateMaxTipWidth(inputRef.current);
      setMaxTipWidth(calculatedWidth);
    };

    updateMaxTipWidth();
    window.addEventListener('resize', updateMaxTipWidth);

    return () => {
      window.removeEventListener('resize', updateMaxTipWidth);
    };
  }, [isAutoAdjustMaxTipWidth, showMaxValueTip]);

  // 测量文本宽度，判断是否需要换行，并计算实际行宽度（使用 useLayoutEffect 在 DOM 更新后立即测量）
  useLayoutEffect(() => {
    if (!showMaxValueTip || !tipTextRef.current || !maxTipWidth) {
      setShouldWrap(false);
      setActualLineWidth(null);
      return;
    }

    const textWidth = measureTextWidth(tipTextRef.current);
    if (textWidth === null) {
      setShouldWrap(false);
      setActualLineWidth(null);
      return;
    }

    // 如果文本宽度超过最大宽度，需要换行
    if (textWidth > maxTipWidth) {
      setShouldWrap(true);
      const calculatedLineWidth = calculateLineWidth(textWidth, maxTipWidth);
      setActualLineWidth(calculatedLineWidth);
    } else {
      setShouldWrap(false);
      setActualLineWidth(null);
    }
  }, [showMaxValueTip, maxTipWidth]);

  // 处理值变化，包含自动矫正逻辑
  const handleValueChange = (newValue: number) => {
    let finalValue = newValue < 1 ? 0 : newValue;

    // 自动矫正：如果超过最大值，自动设置为最大值
    if (actualMaxValue !== undefined && finalValue > actualMaxValue) {
      finalValue = actualMaxValue;
      setCorrectedValue(actualMaxValue);
      setShowMaxValueTip(true);
    } else {
      // 如果新值没有超过最大值，立即隐藏提示
      setShowMaxValueTip(false);
      setCorrectedValue(null);
    }
    onChange(field, finalValue);
  };

  // 当有最大值限制且当前值已达到最大值时，禁用增加按钮
  const isMaxValueReached = actualMaxValue !== undefined && value >= actualMaxValue;

  const inputNumber = (
    <div ref={inputRef} style={{ display: 'inline-block' }}>
      <TeaInputNumber
        min={1}
        max={isMaxValueReached ? actualMaxValue : undefined}
        step={1}
        value={value}
        inputProps={{
          type: 'number',
          onBlur: (e) => {
            e.target.value = e.target.value.replace(/[^0-9]/g, '');
          },
        }}
        className="dbi-download-input-num-hide-spin"
        onInputChange={(inputValue) => {
          const valueNum = Number.parseInt(inputValue, 10);
          if (Number.isNaN(valueNum)) {
            return;
          }
          handleValueChange(valueNum);
        }}
        onChange={(changeValue) => {
          handleValueChange(changeValue);
        }}
      />
    </div>
  );

  // 如果有最大值限制且需要显示提示，则用 Bubble 包裹
  if (actualMaxValue !== undefined && showMaxValueTip && correctedValue !== null) {
    const tipText = formatMessage(
      { id: 'dbi_download_max_value_tip' },
      { maxValue: actualMaxValue },
    );

    const tipStyle = buildTipStyle(shouldWrap, actualLineWidth);

    return (
      <Bubble
        className="dbi-download-bubble"
        error
        visible={showMaxValueTip}
        placement="right"
        style={{ maxWidth: 'none' }}
        content={
          <div className="dbi-download-tip" style={tipStyle}>
            <div className="dbi-download-tip-error" ref={tipTextRef}>
              {tipText}
            </div>
          </div>
        }
      >
        {inputNumber}
      </Bubble>
    );
  }

  return inputNumber;
}
